/* Independent Virelia story UI. Uses no theme framework and never edits prices or cart. */
(() => {
  'use strict';
  if (window.__vireliaStoryUI) return;
  window.__vireliaStoryUI = true;
  const instances = new WeakMap();
  const getRoots = scope => [ ...(scope.matches?.('[data-virelia-story]') ? [scope] : []), ...scope.querySelectorAll('[data-virelia-story]') ];
  function init(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 749px)');
    root.querySelectorAll('[data-vs-flip]').forEach(button => {
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        button.querySelector('.vs-benefit-front')?.setAttribute('aria-hidden', String(expanded));
        button.querySelector('.vs-benefit-back')?.setAttribute('aria-hidden', String(!expanded));
      }, { signal });
    });
    root.querySelectorAll('[data-vs-carousel]').forEach(carousel => {
      const track = carousel.querySelector('.vs-track');
      if (!track) return;
      let timer = null, ready = false, started = false, visible = false, hovered = false, focused = false;
      const step = direction => {
        const first = track.firstElementChild;
        const width = (first?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0);
        track.scrollBy({ left: direction * width, behavior: motion.matches ? 'auto' : 'smooth' });
      };
      carousel.querySelector('[data-vs-prev]')?.addEventListener('click', () => step(-1), { signal });
      carousel.querySelector('[data-vs-next]')?.addEventListener('click', () => step(1), { signal });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); step(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      const stop = () => { clearInterval(timer); timer = null; };
      const sync = () => {
        stop();
        if (!carousel.dataset.vsAutoplay || !ready || !visible || hovered || focused || document.hidden || motion.matches ||
          (carousel.hasAttribute('data-vs-mobile-only') && !mobile.matches)) return;
        timer = setInterval(() => {
          if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) track.scrollTo({ left: 0, behavior: 'smooth' });
          else step(1);
        }, Number(carousel.dataset.vsAutoplay) * 1000);
      };
      const load = () => {
        if (started) return;
        started = true;
        // All handlers are registered before any loading hint is changed.
        const pending = [...carousel.querySelectorAll('img')].map(photo => {
          let kickoff;
          const promise = new Promise((resolve, reject) => {
            let settled = false;
            const remove = () => { photo.removeEventListener('load', loaded); photo.removeEventListener('error', failed); signal.removeEventListener('abort', failed); };
            const failed = () => { if (!settled) { settled = true; remove(); reject(new Error('Story image unavailable')); } };
            const loaded = async () => {
              if (settled) return;
              settled = true; remove();
              if (!photo.naturalWidth) { reject(new Error('Story image empty')); return; }
              try { if (photo.decode) await photo.decode(); resolve(); } catch (error) { reject(error); }
            };
            photo.addEventListener('load', loaded);
            photo.addEventListener('error', failed);
            signal.addEventListener('abort', failed, { once: true });
            kickoff = () => { photo.loading = 'eager'; if (photo.complete) photo.naturalWidth ? loaded() : failed(); };
          });
          return { promise, kickoff };
        });
        const completion = Promise.all(pending.map(item => item.promise));
        completion.then(() => { if (!signal.aborted) { ready = true; sync(); } }).catch(stop);
        pending.forEach(item => item.kickoff());
      };
      carousel.addEventListener('mouseenter', () => { hovered = true; sync(); }, { signal });
      carousel.addEventListener('mouseleave', () => { hovered = false; sync(); }, { signal });
      carousel.addEventListener('focusin', () => { focused = true; sync(); }, { signal });
      carousel.addEventListener('focusout', event => { focused = carousel.contains(event.relatedTarget); sync(); }, { signal });
      document.addEventListener('visibilitychange', sync, { signal });
      motion.addEventListener('change', sync, { signal });
      mobile.addEventListener('change', sync, { signal });
      if ('IntersectionObserver' in window) {
        const near = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { near.disconnect(); load(); }
        }, { rootMargin: '1200px 0px' });
        const view = new IntersectionObserver(entries => { visible = entries.some(entry => entry.isIntersecting); sync(); });
        near.observe(carousel); view.observe(carousel);
        cleanups.push(() => { near.disconnect(); view.disconnect(); });
      } else {
        // Geometry fallback keeps offscreen autoplay paused in older browsers too.
        const visibility = () => { const rect = carousel.getBoundingClientRect(); visible = rect.bottom > 0 && rect.top < innerHeight; sync(); };
        load(); visibility();
        window.addEventListener('scroll', visibility, { signal, passive: true });
        window.addEventListener('resize', visibility, { signal });
      }
      cleanups.push(stop);
    });
    root.querySelectorAll('[data-vs-ticker]').forEach(ticker => {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => ticker.classList.toggle('vs-in-view', entries.some(entry => entry.isIntersecting)));
        observer.observe(ticker); cleanups.push(() => observer.disconnect());
      }
    });
    instances.set(root, () => { controller.abort(); cleanups.forEach(cleanup => cleanup()); instances.delete(root); });
  }
  const boot = scope => getRoots(scope).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true }); else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => getRoots(event.target).forEach(root => instances.get(root)?.()));
})();
